import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mykinesiologue.ma';

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${siteUrl}/prendre-rendez-vous#contactpage`,
    "url": `${siteUrl}/prendre-rendez-vous`,
    "name": "Prendre rendez-vous - MyKinesiologue Marrakech",
    "description": "Contactez-moi pour prendre rendez-vous ou pour toute question sur la kinésiologie. Disponible par email, téléphone ou WhatsApp.",
    "isPartOf": {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`
    },
    "mainEntity": {
      "@type": "LocalBusiness",
      "@id": `${siteUrl}/#organization`
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Scroll to contact section if hash is present
    if (window.location.hash === '#contact') {
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const handleContactClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5iikghia7n1eqcqv1a0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-[#F7F3EE]">
      <SEO
        title="Prendre rendez-vous - MyKinesiologue Marrakech"
        description="Contactez-moi pour prendre rendez-vous ou pour toute question sur la kinésiologie. Disponible par email, téléphone ou WhatsApp."
        keywords="prendre rendez-vous kinésiologue, contact kinésiologue Marrakech, rendez-vous kinésiologie"
        canonical={`${siteUrl}/prendre-rendez-vous`}
        schema={schema}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-[1120px] mx-auto px-4 md:px-8 py-4 md:py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/48f019ad192905b3f4ede24b194a300b/9e4b5e4e7afefada3563c5975e98200f.png" alt="MyKinesiologue" className="h-8 md:h-12" />
          </Link>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-[#1F1F1F] cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} style={{ fontSize: '24px' }}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Accueil</Link>
            <Link to="/approche" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Approche</Link>
            <Link to="/a-qui-sadresse" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>À qui s'adresse</Link>
            <Link to="/presence" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Présence</Link>
            <Link to="/contenus-multimedia" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Contenus multimédia</Link>
            <Link to="/prendre-rendez-vous" className="text-[#1F4D45] font-semibold transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>Prendre rendez-vous</Link>
            <Link to="/faq" className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-300" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>FAQ</Link>
            <a href="#contact" onClick={handleContactClick} className="bg-[#1F4D45] text-[#F7F3EE] px-6 h-11 rounded-2xl flex items-center justify-center whitespace-nowrap hover:bg-[#1F4D45]/90 hover:translate-y-[-1px] transition-all duration-300 cursor-pointer ml-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', fontWeight: '500' }}>Me contacter</a>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#C9C1B8]">
            <div className="px-4 py-6 flex flex-col gap-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Accueil</Link>
              <Link to="/approche" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Approche</Link>
              <Link to="/a-qui-sadresse" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>À qui s'adresse</Link>
              <Link to="/presence" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Présence</Link>
              <Link to="/contenus-multimedia" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Contenus multimédia</Link>
              <Link to="/prendre-rendez-vous" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F4D45] font-medium transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Prendre rendez-vous</Link>
              <Link to="/faq" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1F1F1F] hover:text-[#1F4D45] transition-colors duration-400 py-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>FAQ</Link>
              <a href="#contact" onClick={(e) => { handleContactClick(e); setIsMobileMenuOpen(false); }} className="bg-[#1F4D45] text-[#F7F3EE] px-6 h-12 rounded-2xl flex items-center justify-center whitespace-nowrap cursor-pointer mt-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px' }}>Me contacter</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 md:pt-48 pb-16 md:pb-24 bg-[#F7F3EE]">
        <div className="max-w-[1120px] mx-auto px-4 md:px-8 text-center">
          <div className="inline-block border border-[#C9C1B8] rounded-full px-4 py-2 mb-6 md:mb-8">
            <span className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>Prendre rendez-vous</span>
          </div>
          <h1 className="text-[#1F1F1F] mb-4 md:mb-6 max-w-3xl mx-auto" style={{ fontFamily: 'Fraunces, serif', fontSize: '50px', lineHeight: '1.1', fontWeight: '600' }}>
            Il n'est pas nécessaire d'attendre que tout soit bloqué
          </h1>
          <p className="text-[#5E5A55] max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
            Un inconfort persistant, une tension qui ne se relâche pas, une fatigue qui s'installe, ou simplement l'intuition qu'un ajustement est possible peuvent suffire.
          </p>
        </div>
      </section>

      {/* Un espace pour permettre un mouvement */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="w-full h-[400px] md:h-[500px] rounded-[24px] md:rounded-[32px] overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/624fa60e896df59b3f383b64f18d5ec7.jpg" 
                alt="Un espace pour permettre un mouvement" 
                className="w-full h-full object-cover object-top" 
              />
            </div>
            <div>
              <h2 className="text-[#1F1F1F] mb-6" style={{ fontFamily: 'Fraunces, serif', fontSize: '35px', fontWeight: '600', lineHeight: '1.2' }}>
                Un espace pour permettre un mouvement
              </h2>
              <p className="text-[#5E5A55] mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
                Prendre rendez-vous, c'est s'offrir un temps pour écouter ce que le corps exprime et lui permettre de répondre autrement.
              </p>
              <p className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
                La séance ne force rien. Elle crée les conditions pour que des ajustements puissent émerger lorsque le corps y est disponible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Le déroulement */}
      <section className="py-16 md:py-24 bg-[#F7F3EE]">
        <div className="max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-[#1F1F1F] mb-6" style={{ fontFamily: 'Fraunces, serif', fontSize: '35px', fontWeight: '600', lineHeight: '1.2' }}>
                Le déroulement
              </h2>
              <p className="text-[#5E5A55] mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
                La séance se déroule dans un cadre calme et confidentiel. Elle s'appuie sur le test musculaire et sur des techniques guidées par le corps.
              </p>
              <p className="text-[#5E5A55] mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
                Chaque séance est unique. Le travail s'adapte à ce qui est présent, ici et maintenant.
              </p>
              <p className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
                Certaines personnes ressentent une évolution sensible rapidement, d'autres de manière plus progressive. Dans tous les cas, la séance vise à soutenir une compréhension plus juste et des ajustements durables.
              </p>
            </div>
            <div className="w-full h-[400px] md:h-[500px] rounded-[24px] md:rounded-[32px] overflow-hidden order-1 lg:order-2">
              <img 
                src="https://readdy.ai/api/search-image?query=overhead%20view%20of%20smooth%20stone%20surface%20with%20natural%20veining%20patterns%20in%20warm%20sand%20and%20ivory%20tones%2C%20soft%20natural%20daylight%2C%20minimalist%20material%20detail%20photography%2C%20no%20person%2C%20calm%20serene%20atmosphere%2C%20premium%20quality%2C%20architectural%20approach%2C%20silent%20contemplative%20mood%2C%20high-end%20texture&width=600&height=700&seq=prendre-rdv-stone-surface-unique-v1&orientation=portrait" 
                alt="Le déroulement d'une séance" 
                className="w-full h-full object-cover object-top" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Un cadre clair et respectueux */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="w-full h-[400px] md:h-[500px] rounded-[24px] md:rounded-[32px] overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=abstract%20architectural%20corner%20detail%20with%20clean%20geometric%20angles%20where%20two%20walls%20meet%20in%20warm%20beige%20plaster%20texture%2C%20soft%20natural%20light%20creating%20subtle%20gradient%20shadows%2C%20minimalist%20interior%20photography%2C%20no%20person%2C%20calm%20professional%20atmosphere%2C%20premium%20quality%2C%20silent%20contemplative%20space%2C%20serene%20environment&width=600&height=700&seq=prendre-rdv-corner-architecture-unique-v1&orientation=portrait" 
                alt="Un cadre clair et respectueux" 
                className="w-full h-full object-cover object-top" 
              />
            </div>
            <div>
              <h2 className="text-[#1F1F1F] mb-6" style={{ fontFamily: 'Fraunces, serif', fontSize: '35px', fontWeight: '600', lineHeight: '1.2' }}>
                Un cadre clair et respectueux
              </h2>
              <p className="text-[#5E5A55] mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
                La kinésiologie ne se substitue pas à un suivi médical ou thérapeutique.
              </p>
              <p className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
                Elle s'inscrit dans une démarche d'accompagnement et de prévention du stress, en respectant le rythme et les capacités de chacun.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 bg-[#F7F3EE]">
        <div className="max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-[#1F1F1F] mb-4" style={{ fontFamily: 'Fraunces, serif', fontSize: '35px', lineHeight: '1.2', fontWeight: '600' }}>
              Contact
            </h2>
            <p className="text-[#5E5A55] max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', lineHeight: '1.6' }}>
              Vous pouvez me contacter par message privé ou WhatsApp pour toute question ou pour prendre rendez-vous.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Information */}
            <div>
              <div className="space-y-8">
                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#E9E0D4] rounded-2xl flex-shrink-0">
                    <i className="ri-mail-line text-[#1F4D45]" style={{ fontSize: '24px' }}></i>
                  </div>
                  <div>
                    <h3 className="text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', fontWeight: '600' }}>
                      Email
                    </h3>
                    <a href="mailto:tatiana@mykunesiologue.com" className="text-[#5E5A55] hover:text-[#1F4D45] transition-colors" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', lineHeight: '1.5' }}>
                      tatiana@mykunesiologue.com
                    </a>
                  </div>
                </div>

                {/* Phone / WhatsApp */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#E9E0D4] rounded-2xl flex-shrink-0">
                    <i className="ri-whatsapp-line text-[#1F4D45]" style={{ fontSize: '24px' }}></i>
                  </div>
                  <div>
                    <h3 className="text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', fontWeight: '600' }}>
                      WhatsApp
                    </h3>
                    <a href="https://wa.me/212776664292" target="_blank" rel="noopener noreferrer" className="text-[#5E5A55] hover:text-[#1F4D45] transition-colors" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', lineHeight: '1.5' }}>
                      +212 0776664292
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#E9E0D4] rounded-2xl flex-shrink-0">
                    <i className="ri-map-pin-line text-[#1F4D45]" style={{ fontSize: '24px' }}></i>
                  </div>
                  <div>
                    <h3 className="text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', fontWeight: '600' }}>
                      Adresse
                    </h3>
                    <p className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', lineHeight: '1.5' }}>
                      Marrakech, Maroc
                    </p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#E9E0D4] rounded-2xl flex-shrink-0">
                    <i className="ri-time-line text-[#1F4D45]" style={{ fontSize: '24px' }}></i>
                  </div>
                  <div>
                    <h3 className="text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '17px', fontWeight: '600' }}>
                      Horaires
                    </h3>
                    <p className="text-[#5E5A55]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', lineHeight: '1.5' }}>
                      Lundi - Vendredi : 9h00 - 18h00<br />
                      Sur rendez-vous uniquement
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="mt-12 rounded-2xl overflow-hidden border border-[#C9C1B8]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d215.84!2d-7.9811!3d31.6295!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDM3JzQ2LjIiTiA3wrA1OCc1Mi4wIlc!5e0!3m2!1sfr!2sma!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation MyKinesiologue Marrakech"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-[#E9E0D4] rounded-[24px] md:rounded-[32px] p-8 md:p-12">
                <h2 className="text-[#1F1F1F] mb-6" style={{ fontFamily: 'Fraunces, serif', fontSize: '32px', lineHeight: '1.2', fontWeight: '600' }}>
                  Envoyez-moi un message
                </h2>
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: '500' }}>
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 rounded-2xl border border-[#C9C1B8] bg-[#F7F3EE] text-[#1F1F1F] focus:outline-none focus:border-[#1F4D45] transition-colors text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      placeholder="Votre nom"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: '500' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full h-12 px-4 rounded-2xl border border-[#C9C1B8] bg-[#F7F3EE] text-[#1F1F1F] focus:outline-none focus:border-[#1F4D45] transition-colors text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      placeholder="votre@email.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: '500' }}>
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full h-12 px-4 rounded-2xl border border-[#C9C1B8] bg-[#F7F3EE] text-[#1F1F1F] focus:outline-none focus:border-[#1F4D45] transition-colors text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      placeholder="+212 6 00 00 00 00"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: '500' }}>
                      Sujet
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full h-12 px-4 rounded-2xl border border-[#C9C1B8] bg-[#F7F3EE] text-[#1F1F1F] focus:outline-none focus:border-[#1F4D45] transition-colors text-sm cursor-pointer"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <option value="">Sélectionnez un sujet</option>
                      <option value="Prise de rendez-vous">Prise de rendez-vous</option>
                      <option value="Question sur la kinésiologie">Question sur la kinésiologie</option>
                      <option value="Tarifs et modalités">Tarifs et modalités</option>
                      <option value="Autre demande">Autre demande</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-[#1F1F1F] mb-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: '500' }}>
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 rounded-2xl border border-[#C9C1B8] bg-[#F7F3EE] text-[#1F1F1F] focus:outline-none focus:border-[#1F4D45] transition-colors resize-none text-sm"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      placeholder="Parlez-moi de ce qui vous amène..."
                    ></textarea>
                    <p className="text-[#5E5A55] text-xs mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {formData.message.length}/500 caractères
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#1F4D45] text-[#F7F3EE] h-12 rounded-2xl whitespace-nowrap hover:translate-y-[-2px] transition-all duration-400 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'Inter, sans-serif', fontSize: '16px', fontWeight: '500' }}
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className="bg-[#1F4D45]/10 border border-[#1F4D45] rounded-2xl p-4 text-center">
                      <p className="text-[#1F4D45]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>
                        ✓ Message envoyé avec succès ! Je vous répondrai dans les plus brefs délais.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-300 rounded-2xl p-4 text-center">
                      <p className="text-red-700" style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px' }}>
                        Une erreur est survenue. Veuillez réessayer ou me contacter directement par email.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F4D45] py-12 md:py-16">
        <div className="max-w-[1120px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <Link to="/" className="inline-block mb-4">
                <img src="https://static.readdy.ai/image/48f019ad192905b3f4ede24b194a300b/9e4b5e4e7afefada3563c5975e98200f.png" alt="MyKinesiologue" className="h-10" />
              </Link>
              <p className="text-[#E9E0D4]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                Kinésiologie à Marrakech
              </p>
            </div>
            <div>
              <h3 className="text-[#E9E0D4] uppercase tracking-wider mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', letterSpacing: '1.5px' }}>
                Navigation
              </h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <Link to="/" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Accueil</Link>
                <Link to="/contenus-multimedia" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Contenus multimédia</Link>
                <Link to="/approche" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Approche</Link>
                <Link to="/prendre-rendez-vous" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Prendre rendez-vous</Link>
                <Link to="/a-qui-sadresse" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>À qui s'adresse</Link>
                <Link to="/faq" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>FAQ</Link>
                <Link to="/presence" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>Présence</Link>
              </div>
            </div>
            <div>
              <h3 className="text-[#E9E0D4] uppercase tracking-wider mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', letterSpacing: '1.5px' }}>
                Contact
              </h3>
              <div className="flex flex-col gap-2">
                <a href="mailto:tatiana@mykunesiologue.com" className="text-[#F7F3EE] hover:underline break-all" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>tatiana@mykunesiologue.com</a>
                <a href="https://wa.me/212776664292" target="_blank" rel="noopener noreferrer" className="text-[#F7F3EE] hover:underline" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>+212 0776664292</a>
                <p className="text-[#E9E0D4] mt-2" style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                  Marrakech, Maroc
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-[#F7F3EE]/20 pt-6 text-center">
            <p className="text-[#E9E0D4]" style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
              © 2025 MyKinesiologue. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
